import {useRef, useState} from 'react';

import {useApi} from '~/modules/api';
import {getErrorMessage} from '~/modules/utils';
import {parseEntrantUserIds} from './weeklyChallengeEntrants';

export type EntrantsRequest = {
  challengeId: string;
  weekId: string;
  weekEndDate: string;
  requiredAnswers: Array<{questionId: string; answerId: string}>;
};

export type EntrantsResult =
  | {kind: 'entrants'; csv: string; fileName: string; userCount: number}
  | {kind: 'none'; message: string};

export const useWeeklyChallengeEntrants = () => {
  const api = useApi();

  const [isSearching, setIsSearching] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult] = useState<EntrantsResult | null>(null);

  const searchIdRef = useRef(0);

  const reset = () => {
    searchIdRef.current += 1;
    setIsSearching(false);
    setErrorMsg('');
    setResult(null);
  };

  const search = async (request: EntrantsRequest) => {
    searchIdRef.current += 1;
    const searchId = searchIdRef.current;
    const isCurrent = () => searchId === searchIdRef.current;

    setIsSearching(true);
    setErrorMsg('');
    setResult(null);
    try {
      const {data, headers} = await api.fetch({
        url: '/challenges/getWeeklyChallengeEntrants',
        method: 'POST',
        data: request,
      });

      if (!isCurrent()) {
        return;
      }
      if (typeof data !== 'string') {
        throw new Error('Unexpected response format');
      }
      // A CSV means there are entrants; anything else is a message to show
      if (headers['content-type']?.toString().includes('text/csv')) {
        setResult({
          kind: 'entrants',
          csv: data,
          fileName: `weekly-challenge-${request.challengeId}-${request.weekId}-${
            request.weekEndDate
          }-entrants-${new Date().toISOString()}.csv`,
          userCount: parseEntrantUserIds(data).length,
        });
      } else {
        setResult({kind: 'none', message: data});
      }
    } catch (error) {
      if (isCurrent()) {
        setErrorMsg(getErrorMessage(error, 'Search failed'));
      }
    } finally {
      if (isCurrent()) {
        setIsSearching(false);
      }
    }
  };

  return {isSearching, errorMsg, result, search, reset};
};
